import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, Filter, Search, User } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useAccrualHistory } from '@/hooks/leave/useAccrualHistory';
import { AccrualHistoryFilters, AccrualHistoryWithJoins } from '@/services/leave/accrualHistoryService';
import { useLeaveBalances } from '@/hooks/leave/useLeaveBalances';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';
import { useDebouncedCallback } from 'use-debounce';
import type { LeaveBalanceWithProfile } from '@/services/leave/leavePolicyTypes';

interface AuditTrailViewerProps {
  profileId?: string;
  policyId?: string;
  className?: string;
}

const PAGE_SIZE = 50;

const eventTypeLabels: Record<string, string> = {
  accrual: 'Accrual',
  deduction: 'Deduction',
  adjustment: 'Adjustment', 
  rollover: 'Rollover',
  expiration: 'Expiration',
  policy_change: 'Policy Change',
};

const eventTypeColors: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  accrual: 'default',
  deduction: 'destructive',
  adjustment: 'secondary',
  rollover: 'outline',
  expiration: 'destructive',
  policy_change: 'secondary',
};

export const AuditTrailViewer = ({ profileId, policyId, className }: AuditTrailViewerProps) => {
  const [filters, setFilters] = useState<AccrualHistoryFilters>({
    profile_id: profileId,
    policy_id: policyId,
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [searchInput, setSearchInput] = useState('');
  // Accumulated entries for offset-based append
  const [accumulatedData, setAccumulatedData] = useState<AccrualHistoryWithJoins[]>([]);

  // Sync profileId and policyId props with filter state when they change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      profile_id: profileId,
      policy_id: policyId,
      offset: 0,
    }));
    setAccumulatedData([]);
  }, [profileId, policyId]);

  // Debounced server-side search
  const debouncedSearch = useDebouncedCallback((term: string) => {
    setAccumulatedData([]);
    setFilters(prev => ({
      ...prev,
      search_term: term || undefined,
      offset: 0,
    }));
  }, 300);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const { data: historyData, isLoading, error } = useAccrualHistory(filters);

  // Append new page data when it arrives
  useEffect(() => {
    if (historyData?.data) {
      const offset = filters.offset || 0;
      if (offset === 0) {
        setAccumulatedData(historyData.data);
      } else {
        setAccumulatedData(prev => [...prev, ...historyData.data]);
      }
    }
  }, [historyData, filters.offset]);

  // Fetch leave balances
  const { data: balancesData, isLoading: balancesLoading } = useLeaveBalances(profileId);

  // Group balances by employee for company-wide view
  const groupedBalances = useMemo(() => {
    if (!balancesData || profileId) return null;
    
    const grouped = new Map<string, {
      profile: LeaveBalanceWithProfile['profile'];
      balances: LeaveBalanceWithProfile[];
    }>();
    
    balancesData.forEach((balance) => {
      if (!balance.profile) return;
      const key = balance.profile_id;
      if (!grouped.has(key)) {
        grouped.set(key, {
          profile: balance.profile,
          balances: [],
        });
      }
      grouped.get(key)!.balances.push(balance);
    });
    
    return Array.from(grouped.values());
  }, [balancesData, profileId]);

  const handleFilterChange = (key: keyof AccrualHistoryFilters, value: string | undefined) => {
    setAccumulatedData([]);
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      offset: 0,
    }));
  };

  const handleDateFilter = () => {
    setAccumulatedData([]);
    setFilters(prev => ({
      ...prev,
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      offset: 0,
    }));
  };

  const clearFilters = () => {
    setAccumulatedData([]);
    setSearchInput('');
    setFilters({
      profile_id: profileId,
      policy_id: policyId,
      limit: PAGE_SIZE,
      offset: 0,
    });
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const handleLoadMore = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      offset: (prev.offset || 0) + PAGE_SIZE,
    }));
  }, []);

  const totalCount = historyData?.total || 0;
  const hasMore = accumulatedData.length < totalCount;

  const exportToCSV = () => {
    if (!accumulatedData.length) return;

    const headers = [
      'Date',
      'Employee',
      'Policy',
      'Event Type',
      'Event Subtype',
      'Amount (Days)',
      'Balance Before',
      'Balance After',
      'Description',
      'Performed By',
    ];

    const rows = accumulatedData.map(entry => [
      format(new Date(entry.performed_at), 'yyyy-MM-dd HH:mm'),
      entry.profiles
        ? `${entry.profiles.first_name} ${entry.profiles.last_name}`
        : entry.deleted_employee_name || 'Unknown',
      entry.leave_policies?.name || 'Unknown Policy',
      eventTypeLabels[entry.event_type] || entry.event_type,
      entry.event_subtype || '',
      entry.amount_days || 0,
      entry.balance_before_days || 0,
      entry.balance_after_days || 0,
      entry.description,
      entry.performer
        ? `${entry.performer.first_name} ${entry.performer.last_name}`
        : entry.deleted_performer_name || 'System',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accrual-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (isLoading && accumulatedData.length === 0) {
    return (
      <Card className={cn("p-6", className)}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn("p-6", className)}>
        <div className="text-center text-destructive">
          <p>Error loading audit trail</p>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn("p-6 overflow-hidden", className)}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h3 className="text-lg font-semibold">Leave Accrual Audit Trail</h3>
          <Button onClick={exportToCSV} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Leave Balances Summary */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            {profileId ? 'Employee Leave Balances' : 'Company Leave Balances'}
          </h4>
          
          {balancesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-muted/50 p-4 rounded-lg animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-muted rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : profileId && balancesData && balancesData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {balancesData.map((balance) => (
                <div key={balance.id} className="bg-muted/50 p-4 rounded-lg border">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{balance.policy.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {getLeaveTypeLabel(balance.policy.leave_type)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Balance:</span>
                      <span className="font-semibold text-green-600">
                        {Number(balance.current_balance_days || 0).toFixed(1)} days
                      </span>
                    </div>
                    
                    {(balance.pending_days || 0) > 0 && (
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-muted-foreground">Pending:</span>
                        <span className="text-sm text-amber-600">
                          {Number(balance.pending_days || 0).toFixed(1)} days
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : groupedBalances && groupedBalances.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedBalances.map((group) => (
                <div key={group.balances[0]?.profile_id} className="bg-muted/50 p-4 rounded-lg border">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm">
                      {group.profile?.first_name} {group.profile?.last_name}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    {group.balances.map((balance) => (
                      <div key={balance.id} className="space-y-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{balance.policy.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {getLeaveTypeLabel(balance.policy.leave_type)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-baseline justify-between text-xs">
                          <span className="text-muted-foreground">Balance:</span>
                          <span className="font-semibold text-green-600">
                            {Number(balance.current_balance_days || 0).toFixed(1)} days
                          </span>
                        </div>
                        
                        {(balance.pending_days || 0) > 0 && (
                          <div className="flex items-baseline justify-between text-xs">
                            <span className="text-muted-foreground">Pending:</span>
                            <span className="text-amber-600">
                              {Number(balance.pending_days || 0).toFixed(1)} days
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-muted/50 p-4 rounded-lg text-center text-muted-foreground">
              <p>No leave balances found</p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            <span className="font-medium">Filters</span>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:items-end">
            <div className="w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Search descriptions..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="w-full sm:w-auto sm:min-w-[160px]">
              <Select value={filters.event_type || 'all'} onValueChange={(value) => handleFilterChange('event_type', value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Event Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Event Types</SelectItem>
                  {Object.entries(eventTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-auto sm:min-w-[160px]">
              <Select value={filters.reference_type || 'all'} onValueChange={(value) => handleFilterChange('reference_type', value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Reference Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All References</SelectItem>
                  <SelectItem value="leave_request">Leave Request</SelectItem>
                  <SelectItem value="processing_log">Processing Log</SelectItem>
                  <SelectItem value="manual_adjustment">Manual Adjustment</SelectItem>
                  <SelectItem value="rollover">Rollover</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="min-w-[80px]">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {startDate ? format(startDate, 'MMM dd') : 'From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="min-w-[80px]">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {endDate ? format(endDate, 'MMM dd') : 'To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                  />
                </PopoverContent>
              </Popover>

              <Button variant="outline" size="sm" onClick={handleDateFilter} className="min-w-[60px]">
                Apply
              </Button>
            </div>
          </div>
        </div>

        {/* History Table - Only show when employee is selected */}
        {profileId ? (
          <div className="space-y-4">
            {accumulatedData.length > 0 ? (
              <div className="space-y-2">
                {accumulatedData.map((entry) => (
                  <div key={entry.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="space-y-2 flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <Badge variant={eventTypeColors[entry.event_type] || 'default'}>
                            {eventTypeLabels[entry.event_type] || entry.event_type}
                          </Badge>
                          {entry.event_subtype && (
                            <Badge variant="outline">{entry.event_subtype}</Badge>
                          )}
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(entry.performed_at), 'MMM dd, yyyy HH:mm')}
                          </span>
                        </div>
                        
                        <div className="text-sm">
                          <strong>{entry.description}</strong>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-muted-foreground">
                          {entry.profiles && (
                            <span>Employee: {entry.profiles.first_name} {entry.profiles.last_name}</span>
                          )}
                          {!entry.profiles && entry.deleted_employee_name && (
                            <span>Employee: {entry.deleted_employee_name} (deleted)</span>
                          )}
                          {entry.leave_policies && (
                            <span>Policy: {entry.leave_policies.name}</span>
                          )}
                          {entry.performer ? (
                            <span>By: {entry.performer.first_name} {entry.performer.last_name}</span>
                          ) : entry.deleted_performer_name ? (
                            <span>By: {entry.deleted_performer_name} (deleted)</span>
                          ) : (
                            <span>By: System</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="sm:text-right space-y-1">
                        <div className="text-sm font-medium">
                          {entry.amount_days !== null && entry.amount_days !== undefined && (
                            <span className={cn(
                              entry.amount_days >= 0 ? "text-green-600" : "text-red-600"
                            )}>
                              {entry.amount_days >= 0 ? '+' : ''}{Number(entry.amount_days).toFixed(2)} days
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Balance: {Number(entry.balance_before_days || 0).toFixed(2)} → {Number(entry.balance_after_days || 0).toFixed(2)} days
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No audit trail entries found</p>
                <p className="text-sm mt-1">Try adjusting your filters or date range</p>
              </div>
            )}

            {/* Load More */}
            {hasMore && (
              <div className="text-center">
                <Button 
                  variant="outline" 
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More Entries'}
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  Showing {accumulatedData.length} of {totalCount} entries
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground border rounded-lg bg-muted/20">
            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">Select an employee to view audit trail</p>
            <p className="text-sm mt-1">Choose an employee from the dropdown above to see their detailed history</p>
          </div>
        )}
      </div>
    </Card>
  );
};
